import { Box, Heading, Stack, StackProps, Text } from "@chakra-ui/react";

import { RelatedData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import CustomWrapper from "components/Chakra/CustomWrapper";
import SearchLink from "components/Chakra/SearchLink";
import { TrackEventProps } from "contexts/Tracking";
import { createSearchUrl } from "scenes/Search/createSearchUrl";
import { TFilter } from "scenes/Search/types";
import { IMarginProps, IPaddingProps } from "src/types/types";
import OfferCard from "./OfferCard";

interface IProps extends IMarginProps, IPaddingProps, Pick<StackProps, "maxW"> {
  title?: string;
  filterText?: string;
  offerItems?: RelatedData["offers"];
  linkProps?: {
    location?: string;
    filter: Partial<TFilter>;
    trackEventPayload: TrackEventProps;
  };
  dataTest?: string;
}

const OfferSection: React.FC<IProps> = ({
  title,
  filterText,
  offerItems,
  linkProps,
  dataTest,
  ...style
}) => (
  <CustomWrapper
    stack
    maxW="1186px"
    pr={0}
    contentProps={{ spacing: "24px" }}
    dataTest={dataTest}
    {...style}
  >
    {title && (
      <>
        {filterText ? (
          <Stack spacing={0} pr="16px">
            <Heading as="h2" data-test={`${dataTest}.title`}>
              {title}
            </Heading>
            <Stack
              direction={["column", "row"]}
              justify="space-between"
              spacing={["16px", "unset"]}
              align={["start", "center"]}
              data-test={`${dataTest}.filter`}
            >
              <Text
                variant="small_2"
                maxWidth="45em"
                data-test={`${dataTest}.filter.text`}
              >
                {filterText}
              </Text>
              {linkProps && (
                <SearchLink
                  href={createSearchUrl({
                    filter: {
                      bounds: undefined,
                      ...linkProps,
                    },
                    location: linkProps.location,
                  })}
                  trackEventPayload={linkProps.trackEventPayload}
                  variant="button_outline"
                  data-test={`${dataTest}.filter.showAll`}
                >
                  Zobrazit vše
                </SearchLink>
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack
            spacing={["16px", "unset"]}
            pr="16px"
            direction={["column", "row"]}
            justify="space-between"
            align={["start", "unset"]}
          >
            <Heading as="h3" variant="h2" data-test={`${dataTest}.title`}>
              {title}
            </Heading>
            {linkProps && (
              <SearchLink
                variant="button_outline"
                href={createSearchUrl({
                  filter: {
                    ...linkProps.filter,
                  },
                  location: linkProps.location,
                })}
                trackEventPayload={linkProps.trackEventPayload}
                data-test={`${dataTest}.filter.showAll`}
              >
                Zobrazit vše
              </SearchLink>
            )}
          </Stack>
        )}
      </>
    )}

    <Stack
      direction="row"
      spacing="20px"
      pt="24px"
      pb="16px"
      px={{ md: "4px" }}
      overflowY={{ base: "hidden" }}
      overflowX={{ base: "auto" }}
      data-test={`${dataTest}.results`}
      as="ul"
      listStyleType="none"
    >
      {offerItems?.map((item) => (
        <Box as="li" key={item.id}>
          <OfferCard offer={item} dataTest={`${dataTest}.results.offerCard`} />
        </Box>
      ))}
    </Stack>
  </CustomWrapper>
);

export default OfferSection;
